import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ApiStatus, MaximumSpeedPowerComponentState, StateStatus } from './maximum-speed-power.component.state';
import { Observable } from 'rxjs';
import { IMaximumSpeedPowerSettings } from '@shared/interfaces/metric-settings.interface';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { AppConstants } from '@core/constants';
import { Router } from '@angular/router';

@Component({
  selector: 'app-maximum-speed-power',
  templateUrl: './maximum-speed-power.component.html',
  styleUrls: ['./maximum-speed-power.component.scss'],
  providers: [MaximumSpeedPowerComponentState],
})
export class MaximumSpeedPowerComponent implements OnInit {
  @Input() featureId: number;
  @Input() featureEnabled = false;

  public apiStatus$: Observable<ApiStatus>;
  public settings$: Observable<IMaximumSpeedPowerSettings | null>;
  public stateStatus$: Observable<StateStatus | null>;

  public maximumSpeedPowerForm: FormGroup;

  constructor(
    private readonly fb: FormBuilder,
    private readonly state: MaximumSpeedPowerComponentState,
    private snackBar: MatSnackBar,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.linkState();
    if (this.featureEnabled) {
      this.initForm();
      this.state.loadSettings(this.featureId);
      this.setupListeners();
    }
  }

  initForm(): void {
    this.maximumSpeedPowerForm = this.fb.group({
      speedDuration: [5, [Validators.required, Validators.min(1), Validators.max(5)]],
      speedDistance: ['', [Validators.required, Validators.min(0)]],
      powerDuration: [5, [Validators.required, Validators.min(1), Validators.max(5)]],
      powerWatts: ['', [Validators.required, Validators.min(0)]]
    });
  }

  populateForm(settings: IMaximumSpeedPowerSettings): void {
    this.maximumSpeedPowerForm.patchValue(settings);
  }

  onSave(): void {
    if (!this.maximumSpeedPowerForm.valid) {
      return
    }

    const formData: IMaximumSpeedPowerSettings = this.maximumSpeedPowerForm.value;
    this.state.saveSettings(this.featureId, formData);
  }

  getErrorMessage(field: string): string {
    const control = this.maximumSpeedPowerForm.get(field);
    if (control?.hasError('required')) {
      return `The field id required`;
    }

    switch (field) {
      case 'powerWatts':
      case 'speedDistance': {
        if (control?.hasError('min')) {
          return `Must be greater than or equal to 0`;
        }
        break;
      }
      case 'speedDuration':
      case 'powerDuration': {
        if (control?.hasError('min')) {
          return `Must be greater than or equal to 1`;
        }
        if (control?.hasError('max')) {
          return `Must be less than or equal to 5`;
        }
      }
    }

    return '';
  }

  tryForFreeText(): string {
    return "Activate";
  }

  public purchaseMetric(): void {
    const feature_slug = 'max_power';
    this.router.navigate(['home/feature-store', 'feature-detail'], {
      queryParams: {
        feature_slug,
        tab_index_to_return: 0
      }
    });
  }

  private setupListeners(): void {
    this.settingsListener();
    this.statusListener();
  }

  private settingsListener(): void {
    this.settings$.subscribe(settings => {
      if (!settings) {
        return
      }

      this.populateForm(settings);
    })
  }

  private statusListener(): void {
    this.stateStatus$.subscribe(status => {
      if (!status) {
        return
      }

      switch (status.status) {
        case 'ok':
          this.snackBar.open(status.message, 'OK', AppConstants.TOAST_CONFIG.SUCCESS);
          break
        case 'warning':
          this.snackBar.open(status.message, 'Warning', AppConstants.TOAST_CONFIG.WARNING);
          break
        case 'error':
          this.snackBar.open(status.message, 'Error', AppConstants.TOAST_CONFIG.ERROR);
          break
        default:
          break
      }
    })
  }

  private linkState(): void {
    this.apiStatus$ = this.state.apiStatus$;
    this.settings$ = this.state.settings$;
    this.stateStatus$ = this.state.status$;
  }
}
